import { Box, Typography, Chip } from "@mui/material";
import { styled } from "@mui/material/styles";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { CircleTickIcon, CircleWarningIcon } from "../../components-new/icons";
import { EllipsisTooltipText } from "../../components-new/common";
import { getStepStatus } from "../../utils/helpers";

const Card = styled(Box)({
  flex: 1,
  minWidth: "280px",
  maxWidth: "100%",
  gap: "12px",
  alignItems: "flex-start",
  padding: "20px",
  display: "flex",
  borderRadius: "6px",
  backgroundColor: "#ffffff",
  boxShadow: "0px 43px 62.4px 0px rgba(0, 0, 0, 0.09)",
  flexDirection: "column",
  borderTop: "2px solid #2550d1",
});

const ImageContainer = styled(Box)({
  borderRadius: "4px",
  height: 220,
  width: "100%",
  border: "1px solid rgba(0, 0, 0, 0.30)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  overflow: "hidden",
  "& img": {
    height: "100%",
    width: "100%",
    objectFit: "cover",
  },
});

const StepHeader = styled(Box)({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "12px",
});

const StepNumber = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  color: "#232d3d",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const InfoRow = styled(Box)({
  width: "100%",
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  gap: "16px",
  marginBottom: "12px",
});

const InfoColumn = styled(Box)({
  flex: 1,
  minWidth: "120px",
});

const Label = styled(Typography)({
  fontWeight: 600,
  color: "#232d3d",
  fontSize: "14px",
  marginBottom: "4px",
});

const Value = styled(Typography)({
  color: "#424242",
  fontSize: "14px",
  overflowWrap: "anywhere",
});

const PublicTestStepCard = ({ item }) => {
  const stepStatus = getStepStatus(item);
  return (
    <Card>
      <ImageContainer>
        {item?.Failure_Screenshot_Location ? (
          <img
            src={
              item.Failure_Screenshot_Location || `https://via.placeholder.com/900?text=No+Image`
            }
            alt="testImage"
          />
        ) : (
          <ImageNotSupportedOutlinedIcon sx={{ color: "#8089A8" }} fontSize="large" />
        )}
      </ImageContainer>

      <StepHeader>
        <StepNumber>
          Step {item?.Step_Number}
          {/* Only show an icon for a known status - a missing one must never read as a pass */}
          {stepStatus === "PASS" && <CircleTickIcon />}
          {stepStatus === "FAIL" && <CircleWarningIcon />}
        </StepNumber>
      </StepHeader>

      <InfoRow>
        <InfoColumn>
          <Label>Status</Label>
          <Value>{stepStatus || "-"}</Value>
        </InfoColumn>
        <InfoColumn>
          <Label>Duration</Label>
          <Value>{item?.test_step_duration || "-"}</Value>
        </InfoColumn>
      </InfoRow>

      <InfoRow>
        <InfoColumn>
          <Label>Keyword</Label>
          <Value>{item?.Keyword || "-"}</Value>
        </InfoColumn>
        <InfoColumn>
          <Label>Action</Label>
          <Value>{item?.Action || "-"}</Value>
        </InfoColumn>
      </InfoRow>

      <InfoRow>
        <InfoColumn>
          <Label>Test Data</Label>
          <Value>
            <EllipsisTooltipText
              text={item.TestData ? item.TestData : "-"}
              maxChars={80}
              mode="chars"
            />
          </Value>
        </InfoColumn>
      </InfoRow>

      {item?.self_healed && (
        <InfoRow>
          <InfoColumn>
            <Label>Self Healed</Label>
            <Chip
              icon={<AutoFixHighIcon style={{ fontSize: "16px" }} />}
              label={`Healed from: ${item.self_healed_from}`}
              color="warning"
              variant="outlined"
              size="small"
              sx={{ maxWidth: "100%" }}
            />
          </InfoColumn>
        </InfoRow>
      )}

      {stepStatus === "FAIL" && (
        <>
          <InfoRow>
            <InfoColumn>
              <Label>Issue Type</Label>
              <Value>
                <EllipsisTooltipText
                  text={item.test_step_issuetype ? item.test_step_issuetype : "-"}
                  maxChars={80}
                  mode="chars"
                />
              </Value>
            </InfoColumn>
          </InfoRow>
          <InfoRow>
            <InfoColumn>
              <Label>Issues</Label>
              <Value>
                <EllipsisTooltipText
                  text={item.test_step_issues ? item.test_step_issues : "-"}
                  maxChars={80}
                  mode="chars"
                />
              </Value>
            </InfoColumn>
          </InfoRow>
        </>
      )}
    </Card>
  );
};

export default PublicTestStepCard;
